import {
  DKDataGrid,
  DKButton,
  DKIcons,
  DKIcon,
  DKLabel,
  DKIconText,
  INPUT_TYPE,
  showAlert
} from 'deskera-ui-library';
import { PRODUCTS_COLUMNS } from '../constants/ColumnConfigs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deepClone, isEmptyObject } from '../utility/Helper';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchLoggedInContact,
  selectLoggedInContact
} from '../store/slices/booksSlice';
import {
  fetchProductCustomFields,
  prepareSalesOrderPayload
} from '../services/common/order';
import { createSalesOrder } from '../services/books';
import { fetchCartItems, selectCartItems } from '../store/slices/cartSlice';
import { amountRenderer } from '../utility/renderer';
import { deleteFromCart } from '../services/cart';
import ic_no_data from '../assets/icons/ic_no_data.png';
import type { Attachment } from '../pages/Attachment';
import {
  EXTENSIONS_FILE_ALLOWED_UPLOAD,
  MAX_FILE_SIZE,
  fetchAttachments,
  getHumanReadableFileSize,
  openFileBrowser,
  uploadFileToAWS
} from '../services/common/files';
import { SALES_ORDER_ATTACHMENT_LIMIT } from '../constants/Constant';
import { notifySalesOrderCreated } from '../services/events/salesOrderEvents';
import { getCartItemCount, shouldResetAttachments } from '../utility/orderDraft';

interface IOrderSummaryProps {
  onSave: () => void;
  onClose: () => void;
}

const OrderSummary: React.FC<IOrderSummaryProps> = (props) => {
  const [rows, setRows] = useState<any[]>([]);
  const cartItems = useAppSelector(selectCartItems);
  const contactInfo = useAppSelector(selectLoggedInContact);
  const dispatch = useAppDispatch();
  const [apiCallInProgress, updateApiCallFlag] = useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [attachmentUploadInProgress, setAttachmentUploadInProgress] =
    useState<boolean>(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState<boolean>(false);
  const [availableAttachments, setAvailableAttachments] = useState<Attachment[]>(
    []
  );
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>(
    []
  );
  const cartItemCount = getCartItemCount(cartItems);
  const cartRows = useMemo(() => {
    if (Array.isArray(cartItems?.data)) {
      return cartItems.data;
    }
    if (Array.isArray(cartItems)) {
      return cartItems;
    }
    return [];
  }, [cartItems]);
  const previousCartCountRef = useRef<number>(cartItemCount);
  const clearAttachmentSelection = useCallback(() => {
    setSelectedAttachmentIds([]);
  }, []);
  const resetDraftState = useCallback(() => {
    clearAttachmentSelection();
    setSelectedProducts([]);
  }, [clearAttachmentSelection]);

  useEffect(() => {
    if (isEmptyObject(contactInfo)) {
      dispatch(fetchLoggedInContact({}));
    }
    dispatch(fetchCartItems());
  }, []);

  useEffect(() => {
    setRows(deepClone(cartRows ?? []));
  }, [cartRows]);

  useEffect(() => {
    if (
      shouldResetAttachments(previousCartCountRef.current, cartItemCount)
    ) {
      resetDraftState();
    }
    previousCartCountRef.current = cartItemCount;
  }, [cartItemCount, resetDraftState]);

  useEffect(() => {
    if (contactInfo?.id) {
      fetchAvailableAttachments();
    }
  }, [contactInfo?.id]);

  const onOrderPlace = () => {
    if (!apiCallInProgress) {
      if (!rows.length) {
        showAlert('Add Products', 'Please select at least one product.');
        return;
      }
      const sanitizedAttachmentIds = selectedAttachmentIds
        .map((id: string) => Number(id))
        .filter((id: number) => !Number.isNaN(id));
      updateApiCallFlag(true);
      fetchProductCustomFields(rows, contactInfo?.code)
        .then((customFieldsByCode) =>
          createSalesOrder(
            prepareSalesOrderPayload(rows, contactInfo, {
              attachmentIds: sanitizedAttachmentIds,
              customFieldsByCode
            })
          )
        )
        .then((res: any) => {
          if (res) {
            console.log(res);

            emptyCart();
            showAlert(
              'Success!',
              `Order <b>${res.documentSequenceCode}</b> Placed Successfully.`
            );
            notifySalesOrderCreated({
              documentSequenceCode: res.documentSequenceCode
            });
            props.onSave?.();
            clearAttachmentSelection();
          }
        })
        .catch((err: any) => {
          console.error('err', err);
          showAlert('Error!', 'Error occurred, Please try again!');
        })
        .finally(() => {
          updateApiCallFlag(false);
        });
    }
  };

  const browseAttachment = () => {
    if (attachmentUploadInProgress) return;
    openFileBrowser(onNewAttachmentSelected, {
      accept: Object.values(EXTENSIONS_FILE_ALLOWED_UPLOAD).join(','),
      multiple: true
    });
  };

  const onNewAttachmentSelected = (files: File[]) => {
    if (!files?.length) return;
    const fileList = Array.from(files);
    const exceedingFiles = fileList.filter(
      (file) => file.size > MAX_FILE_SIZE
    );
    if (exceedingFiles.length) {
      showAlert(
        'File too large',
        `Each file must be under ${getHumanReadableFileSize(MAX_FILE_SIZE)}.`
      );
      return;
    }
    uploadFiles(fileList);
  };

  const uploadFiles = (files: File[]) => {
    if (!contactInfo?.id) {
      showAlert('Contact missing', 'Unable to identify contact for upload.');
      return;
    }
    setAttachmentUploadInProgress(true);
    Promise.all(
      files.map((file) => uploadFileToAWS(file, contactInfo.id, 'CONTACT'))
    )
      .then(() => {
        fetchAvailableAttachments();
      })
      .catch(() => {
        showAlert('Upload failed', 'Please try uploading the files again.');
      })
      .finally(() => setAttachmentUploadInProgress(false));
  };

  const fetchAvailableAttachments = () => {
    if (!contactInfo?.id) {
      return;
    }
    setAttachmentsLoading(true);
    fetchAttachments({ entityId: contactInfo.id, entityName: 'CONTACT' })
      .then((list: Attachment[] = []) => {
        setAvailableAttachments(list || []);
        setSelectedAttachmentIds((prev: string[]) =>
          prev.filter((id: string) =>
            list?.some((item) => String(item.attachmentId) === id)
          )
        );
      })
      .catch(() => {
        showAlert('Unable to load attachments', 'Please try again later.');
      })
      .finally(() => setAttachmentsLoading(false));
  };

  const toggleAttachmentSelection = (attachment: Attachment) => {
    const attachmentId = String(attachment.attachmentId);
    setSelectedAttachmentIds((prev: string[]) => {
      const exists = prev.includes(attachmentId);
      if (exists) {
        return prev.filter((id: string) => id !== attachmentId);
      }
      if (prev.length >= SALES_ORDER_ATTACHMENT_LIMIT) {
        showAlert(
          'Attachment limit reached',
          `You can select up to ${SALES_ORDER_ATTACHMENT_LIMIT} attachments.`
        );
        return prev;
      }
      return [...prev, attachmentId];
    });
  };

  const getAttachmentSizeLabel = (attachment: Attachment) => {
    if (!attachment?.attachmentSize) {
      return 'Size unavailable';
    }
    const numericSize = Number(attachment.attachmentSize);
    if (!Number.isNaN(numericSize)) {
      return getHumanReadableFileSize(numericSize);
    }
    return attachment.attachmentSize;
  };

  const getHeader = () => {
    return (
      <div className="row-reverse p-h-l p-v-s bg-gray1" style={{ gap: 10 }}>
        {(cartItems?.data?.length || 0) > 0 && <DKIconText
          text={'Place Order'}
          icon={apiCallInProgress ? DKIcons.ic_loading : null}
          className={`border-m row border-radius-m text-wrap-none p-s dk-button-hover ${
            apiCallInProgress
              ? 'bg-gray1 cursor-not-allowed '
              : 'bg-button text-white cursor-hand'
          }`}
          iconClassName={'Loading-logo'}
          onClick={onOrderPlace}
          disabled={apiCallInProgress}
        />}
        <DKButton
          title={'Close'}
          className="bg-white border-m"
          onClick={props?.onClose}
        />
      </div>
    );
  };

  const onRowUpdate = ({ rowData, rowIndex, columnData, value }: any) => {
    const copyOfRows = deepClone(rows);
    if (columnData.columnCode === 'cartQuantity') {
      rowData['cartQuantity'] = isNaN(rowData['cartQuantity'])
        ? 0
        : Number(rowData['cartQuantity']);
    }
    copyOfRows[rowIndex] = rowData;
    setRows(copyOfRows);
  };

  const onDelete = ({ rowData, rowIndex }: any) => {
    const copyOfRows = deepClone(rows);
    copyOfRows.splice(rowIndex, 1);
    setRows(copyOfRows);
    deleteItems([rowData.documentSequenceCode]);
  };
  const deleteItems = (productIds: string[]) => {
    deleteFromCart(productIds)
      .then((res) => {
        dispatch(fetchCartItems());
        setSelectedProducts([]);
      })
      .finally(() => {});
  };

  const getUpdatedColumns = () => {
    const columns: any = deepClone(PRODUCTS_COLUMNS);
    columns.push({
      id: 'action',
      key: 'action',
      name: '',
      type: INPUT_TYPE.BUTTON,
      width: 140,
      options: [
        {
          icon: DKIcons.ic_delete,
          className: ' p-0',
          onClick: onDelete
        }
      ]
    });
    return columns?.map((column: any) => {
      switch (column.columnCode) {
        case 'description':
          column.width = 120;
          break;
        case 'quantity':
          column.columnCode = 'cartQuantity';
          column.key = 'cartQuantity';
          column.editable = true;
          break;
        case 'salesPrice':
          column['renderer'] = (data: any) =>
            amountRenderer(data, 'salesPrice');
          break;
        default:
          break;
      }
      return column;
    });
  };

  const onRowSelect = ({ rowData }: any) => {
    const indexOfExisting = selectedProducts?.findIndex(
      (product: any) =>
        product?.documentSequenceCode === rowData?.documentSequenceCode
    );
    const copyOfSelectedProducts = deepClone(selectedProducts);
    if (indexOfExisting === -1) {
      copyOfSelectedProducts.push(rowData);
    } else {
      copyOfSelectedProducts.splice(indexOfExisting, 1);
    }

    setSelectedProducts(copyOfSelectedProducts);
  };
  const onAllRowSelect = ({ selected }: any) => {
    if (selected) {
      setSelectedProducts([...deepClone(rows)]);
    } else {
      setSelectedProducts([]);
    }
  };
  const emptyCart = () => {
    if (!rows.length) {
      resetDraftState();
      return;
    }
    resetDraftState();
    deleteItems(rows.map((product) => product?.documentSequenceCode));
  };
  const getNoDataView = () => {
    let noDataTitle = '';
    let noDataText = '';

    if (apiCallInProgress) {
      noDataTitle = 'Loading...';
      noDataText = 'Please wait';
    } else {
      if (!rows.length) {
        noDataTitle = 'No data found';
        noDataText = 'Once data is available, it will appear here';
      }
    }
    return rows.length ? null : (
      <div
        className="column justify-self-center align-self-center align-items-center position-absolute"
        style={{ top: 200, pointerEvents: 'none' }}
      >
        <DKIcon src={ic_no_data} className="ic-l opacity-20" />
        <DKLabel text={noDataTitle} className="fw-m mt-l" />
        <DKLabel text={noDataText} className="text-gray mt-s" />
      </div>
    );
  };

  const showContextMenu = !isEmptyObject(selectedProducts);

  const renderAttachmentCard = (attachment: Attachment) => {
    const attachmentId = String(attachment.attachmentId);
    const isSelected = selectedAttachmentIds.includes(attachmentId);
    return (
      <div
        key={attachment.attachmentId}
        className={`border-m border-radius-m p-m column cursor-hand ${
          isSelected ? 'bg-gray1 border-green' : 'bg-white'
        }`}
        style={{ width: 220, gap: 6 }}
        onClick={() => toggleAttachmentSelection(attachment)}
      >
        <div className="row justify-content-between align-items-center width-auto">
          <DKIcon
            src={DKIcons.ic_attachment}
            className={`ic-s ${isSelected ? 'text-green' : 'text-gray'}`}
          />
          {isSelected && (
            <DKIcon src={DKIcons.ic_check} className="ic-s text-green" />
          )}
        </div>
        <DKLabel
          className="text-ellipsis fw-m"
          text={attachment.attachmentFileName}
        />
        <DKLabel
          className="text-gray fs-s"
          text={getAttachmentSizeLabel(attachment)}
        />
        <DKLabel
          className={`fs-s ${isSelected ? 'text-green fw-m' : 'text-gray'}`}
          text={isSelected ? '✓ Selected' : 'Tap to attach'}
        />
      </div>
    );
  };

  const renderAttachmentsSection = () => {
    if (!rows.length) {
      return null;
    }
    return (
      <section className="column p-h-l" style={{ gap: 12 }}>
        <div
          className="row justify-content-between align-items-center flex-wrap"
          style={{ gap: 12 }}
        >
          <div className="row align-items-center" style={{ gap: 8 }}>
            <DKIcon src={DKIcons.ic_attachment} className="ic-s" />
            <div className="column" style={{ gap: 2 }}>
              <DKLabel text="Attachments" className="fw-m" />
              <DKLabel
                className="text-gray fs-s"
                text={`Selected ${selectedAttachmentIds.length}/${SALES_ORDER_ATTACHMENT_LIMIT}`}
              />
            </div>
          </div>
          <div className="row flex-wrap" style={{ gap: 8 }}>
            {!!selectedAttachmentIds.length && (
              <DKButton
                title="Clear"
                className="border-m bg-white"
                onClick={clearAttachmentSelection}
              />
            )}
            <DKButton
              title={
                attachmentUploadInProgress ? 'Uploading…' : '+ Upload Files'
              }
              className={`border-m ${
                attachmentUploadInProgress ? 'bg-gray1' : 'bg-white'
              }`}
              onClick={browseAttachment}
              disabled={attachmentUploadInProgress}
            />
          </div>
        </div>
        {attachmentsLoading && (
          <DKLabel className="text-gray" text="Loading attachments…" />
        )}
        {!attachmentsLoading && !availableAttachments.length && (
          <DKLabel
            className="text-gray"
            text="No attachments uploaded yet. Use the button above to add files."
          />
        )}
        {!attachmentsLoading && !!availableAttachments.length && (
          <div className="row flex-wrap" style={{ gap: 12 }}>
            {availableAttachments.map((attachment) =>
              renderAttachmentCard(attachment)
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="transparent-background">
      <div
        className="popup-window overflow-hidden"
        style={{
          maxHeight: '95%',
          height: '75%',
          width: '70%',
          maxWidth: '90%',
          padding: 0
        }}
      >
        <>{getHeader()}</>
        {renderAttachmentsSection()}
        {showContextMenu && (
          <DKButton
            title={'Delete'}
            icon={DKIcons.white.ic_delete}
            className="bg-red border-m text-white ml-l mt-l"
            onClick={() =>
              deleteItems(
                selectedProducts.map((product) => product?.documentSequenceCode)
              )
            }
          />
        )}
        {rows?.length !== 0 && (
          <div className="row p-h-l overflow-visible align-items-start flex-1">
            <DKDataGrid
              title="🛒 Order Summary"
              columns={getUpdatedColumns()}
              rows={rows}
              needTrailingColumn={true}
              allowSearch={false}
              needBoldTheme={true}
              onRowUpdate={onRowUpdate}
              allowBulkOperation={false}
              onRowSelect={onRowSelect}
              showHeader={!showContextMenu}
              onAllRowSelect={onAllRowSelect}
              isAllRowSelected={
                selectedProducts?.length !== 0 &&
                rows?.length === selectedProducts?.length
              }
              buttons={[
                {
                  title: 'Empty Cart',
                  icon: DKIcons.white.ic_delete,
                  className: 'bg-red border-m text-white ml-l mt-l',
                  onClick: emptyCart
                }
              ]}
            />
          </div>
        )}
        {rows?.length === 0 && getNoDataView()}
      </div>
    </div>
  );
};

export default OrderSummary;
